import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import IconButton from '@mui/material/IconButton';
import EditNoteSharpIcon from '@mui/icons-material/Edit';
import VisibilityIcon from "@mui/icons-material/Visibility";
import axiosInstance from '../../../Config/axiosInstance';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button } from '@mui/material';
import "./MonthlyPerformance.css";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import DetailsMonthlyPerformance from './DetailsMonthlyPerformance';

function MonthlyPerformance() {
  const [canCreate, setCanCreate] = useState(false);
  const [canView, setCanView] = useState(false);
  const [rows, setRows] = useState([]);
  const [popupOpen, setPopupOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const onClose = () => setPopupOpen(false);
  const animatedComponents = makeAnimated();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const requiredPath = "/monthly-percent";
    const storedPermissions = JSON.parse(localStorage.getItem("permissions"));
    const permissions = storedPermissions?.pages || [];

    setCanCreate(permissions.some((page) => new URL(page.url).pathname === requiredPath && page.can_create));
    setCanView(permissions.some((page) => new URL(page.url).pathname === requiredPath && page.can_view));
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          monthlyPerformance,
          projectBudget,
          financialPeriod,
          budgetStructure
        ] = await Promise.all([
          axiosInstance.get('/monthly-performance/'),
          axiosInstance.get('/project-budget/'),
          axiosInstance.get('/financial-period/'),
          axiosInstance.get('/budget-structure/')
        ]);

        const PBtCostDescriptionMap = projectBudget.data.reduce((acc, project_budget) => {
          acc[project_budget.id] = project_budget.cost_element;
          return acc;
        }, {});

        const PBtCostIdMap = projectBudget.data.reduce((acc, project_budget) => {
          acc[project_budget.id] = project_budget.cost_element;
          return acc;
        }, {});

        const PBProjectCodeMap = projectBudget.data.reduce((acc, project_budget) => {
          acc[project_budget.id] = project_budget.cost_element;
          return acc;
        }, {});

        const financialPeriodMap = financialPeriod.data.reduce((acc, financial_period) => {
          acc[financial_period.id] = financial_period.financial_period;
          return acc;
        }, {});

        const CostDescriptionMap = budgetStructure.data.reduce((acc, item) => {
          acc[item.id] = item.cost_element_description;
          return acc;
        }, {});

        const CostIdMap = budgetStructure.data.reduce((acc, item) => {
          acc[item.id] = item.cost_element_id;
          return acc;
        }, {});

        const ProjectCodeMap = budgetStructure.data.reduce((acc, item) => {
          acc[item.id] = item.project_code_details?.project_code ?? '-';
          return acc;
        }, {});


        // Grupăm după cost, păstrând doar înregistrarea cu cel mai mare completion_percentage
        const highestCompletionMap = monthlyPerformance.data.reduce((acc, item) => {
          const current = acc[item.cost_id];
          if (!current || item.completion_percentage > current.completion_percentage) {
            acc[item.cost_id] = item;
          }
          return acc;
        }, {});

        // Convertim în array și completăm datele pentru afisare
        const sortedData = Object.values(highestCompletionMap).map((item) => ({
          id: item.id,
          project_code: ProjectCodeMap[PBProjectCodeMap[item.cost_id]] || '-',
          cost_element_id: CostIdMap[PBtCostIdMap[item.cost_id]] || '-',
          cost_element_description: CostDescriptionMap[PBtCostDescriptionMap[item.cost_id]] || '-',
          financial_period: financialPeriodMap[item.financial_period],
          percentage: item.percentage,
          completion_percentage: item.completion_percentage,
          created_at: item.created_at,
          cost_id: item.cost_id
        })).sort((a, b) => b.id - a.id);
        setRows(sortedData);
      } catch (error) {
        console.error('Eroare la obținerea datelor:', error.response?.data || error.message);
      }
    };

    fetchData();
  }, [location.pathname]);

  // Funcție pentru formatarea datei în dd.mm.yyyy HH:MM
  const formatDateTime = (dateString) => {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');

      return `${day}.${month}.${year} ${hours}:${minutes}`;
    } catch (error) {
      console.error('Eroare la formatarea datei:', error);
      return dateString;
    }
  };

  // perioada financiara
  const [financiarPeriodOptions, setFinanciarPeriod] = useState([]);
  useEffect(() => {
    axiosInstance
      .get("/financial-period/")
      .then((response) => {
        const financiarPeriodOptions = response.data.map((financiarPeriod) => ({
          value: financiarPeriod.id,
          label: `${financiarPeriod.month} - ${financiarPeriod.year}`
        }));
        setFinanciarPeriod(financiarPeriodOptions);
      })
      .catch((error) => console.error("Error fetching financial periods:", error));
  }, []);


  const handleEditClick = (row) => {
    setFormData({
      ...row,
      cost_id: row.cost_id || 'n/a',
      percentage: '',
    });

    setPopupOpen(true);
  };
  const [monthWarning, setMonthWarning] = useState("");
  const [percentageWarning, setPercentageWarning] = useState("");


  const handleSave = async () => {
    setMonthWarning("");
    setPercentageWarning("");

    if (!formData.financial_period) {
      setMonthWarning("⚠️ The 'Month' field is required.");
      return;
    }

    if (!formData.percentage) {
      setPercentageWarning("⚠️ The 'Monthly Percentage' field is required.");
      return;
    }

    try {
      const newData = {
        cost_id: formData.cost_id,
        financial_period: formData.financial_period,
        percentage: formData.percentage,
      };

      console.log("Salvarea datelor: ", newData);

      await axiosInstance.post('/monthly-performance/', newData);

      setPopupOpen(false);
      window.location.reload();
    } catch (error) {
      console.error('Eroare la salvare:', error.response?.data || error.message);
      alert('A apărut o eroare la salvare!');
    }
  };


  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };


  const columns = [
    { field: 'project_code', headerName: 'Project Code', flex: 1, minWidth: 220 },
    { field: 'cost_element_id', headerName: 'Cost ID', flex: 1, minWidth: 110 },
    { field: 'cost_element_description', headerName: 'Cost Description', flex: 1.6, minWidth: 250 },
    { field: 'percentage', headerName: 'Monthly Percentage', flex: 1, minWidth: 110 },
    { field: 'completion_percentage', headerName: 'Completion Percentage', flex: 1, minWidth: 110 },
  ];

  if (canCreate || canView) {
    columns.push({
      field: 'action',
      headerName: 'Action',
      width: 100,
      renderCell: (params) => (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "6px" }}>
          {canCreate && (
            <IconButton onClick={() => handleEditClick(params.row)}>
              <EditNoteSharpIcon sx={{ color: '#1976d2' }} />
            </IconButton>
          )}
          {canView && (
            <IconButton onClick={() => navigate(`/monthly-percent/details-monthly-percent/${params.row.id}`)}>
              <VisibilityIcon sx={{ color: '#1976d2' }} />
            </IconButton>
          )}
        </div>
      ),
      sortable: false,
      disableColumnMenu: true,
    });
  }

  return (
    <Routes>
      <Route path="/" element={
        <div className="monthly-percentage-page">
          <div className="monthly-percentage-container">
            <h2 className="title">MONTHLY PERCENT</h2>
          </div>
          <div className="table-container">
            <DataGrid
              id="monthly-percentage-table"
              rows={rows}
              columns={columns}
              getRowHeight={() => "auto"}
              pageSize={5}
              initialState={{
                sorting: {
                  sortModel: [{ field: "id", sort: "desc" }],
                },
                pagination: {
                  paginationModel: { pageSize: 12 },
                },
              }}
              disableRowSelectionOnClick
              sx={{
                "& .MuiDataGrid-cell": {
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: "1.4",
                  display: "flex",
                  alignItems: "center",
                },
                "& .MuiDataGrid-columnHeaders": {
                  whiteSpace: "normal",
                },
                "& .MuiDataGrid-cell:focus": {
                  outline: "none",
                },
                "& .MuiDataGrid-cell:focus-within": {
                  outline: "none",
                },
                "& .Mui-selected:hover": {
                  backgroundColor: "transparent !important",
                },
              }}
              pageSizeOptions={[10, 12, 20, 25]}
            />
          </div>

          {/* Dialog pentru editare */}
          <Dialog
            open={popupOpen}
            onClose={() => setPopupOpen(false)}
            maxWidth="sm"
            fullWidth
            className="add-popup-monthly-percentage-container"
          >
            <DialogTitle className="title-popup">Edit Percentage</DialogTitle>
            <DialogContent dividers>
              <div className="form-group">

                <label htmlFor="project_code" className="label">Project Code</label>
                <input
                  type="text"
                  id="project_code"
                  name="project_code"
                  value={formData.project_code || ''}
                  onChange={handleChange}
                  className="input"
                  disabled
                />
              </div>

              <div className="form-group">
                <label htmlFor="cost_id" className="label">Cost ID</label>
                <input
                  type="text"
                  id="cost_id"
                  name="cost_id"
                  value={formData.cost_element_id || ''}
                  onChange={handleChange}
                  className="input"
                  disabled
                />
              </div>

              <div className="form-group">
                <label htmlFor="created_at" className="label">Created At</label>
                <input
                  type="text"
                  id="created_at"
                  name="created_at"
                  value={formData.created_at ? formatDateTime(formData.created_at) : ''}
                  className="input"
                  disabled
                />
              </div>

              <div className="form-group">
                <label htmlFor="financial_period" className="label">Month <span style={{ color: 'red' }}>*</span> </label>
                <Select
                  id="financial_period"
                  name="financial_period"
                  classNamePrefix="react-select"
                  options={financiarPeriodOptions}
                  value={financiarPeriodOptions.find((c) => c.value === formData.financial_period) || null}
                  onChange={(selectedOption) => {
                    setFormData({ ...formData, financial_period: selectedOption ? selectedOption.value : "" });
                    if (selectedOption) {
                      setMonthWarning("");
                    }
                  }}
                  components={animatedComponents}
                  isClearable
                  styles={{
                    control: (provided) => ({ ...provided, borderRadius: "8px" }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999, // Set z-index to ensure the dropdown is above other elements
                    }),
                  }}
                  menuPortalTarget={document.body} // Place the dropdown menu in the body element
                />
                {monthWarning && (
                  <div style={{ color: 'red', marginTop: '0.5rem' }}>
                    {monthWarning}
                  </div>
                )}
              </div>


              <div className="form-group">
                <label htmlFor="percentage" className="label">Monthly Percentage <span style={{ color: 'red' }}>*</span></label>
                <input
                  type="text"
                  id="percentage"
                  name="percentage"
                  value={formData.percentage || ''}
                  onChange={handleChange}
                  className="input"
                />
                {percentageWarning && (
                  <div style={{ color: 'red', marginTop: '0.5rem' }}>
                    {percentageWarning}
                  </div>
                )}
              </div>

            </DialogContent>

            <DialogActions>
              <Button onClick={onClose} className="button discard">
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSave} className="button save">
                Save
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      } />
      <Route path="details-monthly-percent/:id" element={<DetailsMonthlyPerformance />} />
    </Routes>
  );

}

export default MonthlyPerformance;